'use client';

import { useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

type Method = 'lmp' | 'conception' | 'ivf3' | 'ivf5';

const DAYS_TO_ADD: Record<Method, number> = {
  lmp: 280, // Naegele's rule: last menstrual period + 280 days
  conception: 266, // conception date + 266 days
  ivf3: 263, // day-3 embryo transfer: 266 - 3 days of embryo age
  ivf5: 261, // day-5 blastocyst transfer: 266 - 5 days of embryo age
};

const METHOD_LABELS: Record<Method, string> = {
  lmp: 'First day of last period',
  conception: 'Conception date',
  ivf3: 'IVF transfer date (3-day embryo)',
  ivf5: 'IVF transfer date (5-day embryo)',
};

function addDays(date: Date, days: number) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function formatDate(date: Date) {
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function BrowserDueDateCalculator({
  config,
}: {
  config?: { toolSlug?: string; heading?: string };
}) {
  const fired = useRef(false);
  const fireUsage = () => {
    if (fired.current) return;
    fired.current = true;
    void consumeToolUsage(config?.toolSlug ?? 'due-date-calculator', '').catch(() => undefined);
  };

  // Start 8 weeks back, a typical first-appointment date, so the first screen
  // shows a real result instead of "0 weeks, 0 days".
  const [method, setMethod] = useState<Method>('lmp');
  const [startDate, setStartDate] = useState(() =>
    new Date(Date.now() - 56 * 86_400_000).toISOString().slice(0, 10),
  );

  function onChange<T>(setter: (v: T) => void) {
    return (v: T) => {
      fireUsage();
      setter(v);
    };
  }

  const result = useMemo(() => {
    const start = new Date(`${startDate}T00:00:00`);
    if (Number.isNaN(start.getTime())) return null;

    const dueDate = addDays(start, DAYS_TO_ADD[method]);
    // Pregnancy weeks are conventionally counted from the LMP, so conception-based
    // methods need their reference date shifted back to what the LMP would have been.
    const gestationalStart =
      method === 'lmp'
        ? start
        : method === 'conception'
          ? addDays(start, -14)
          : method === 'ivf3'
            ? addDays(start, -14 - 3)
            : addDays(start, -14 - 5);

    const today = new Date();
    const daysPregnant = Math.floor((today.getTime() - gestationalStart.getTime()) / 86400000);
    const weeksPregnant = Math.floor(daysPregnant / 7);
    const daysRemainder = daysPregnant % 7;
    const trimester = weeksPregnant < 13 ? 1 : weeksPregnant < 27 ? 2 : 3;

    return { dueDate, weeksPregnant, daysRemainder, trimester, daysPregnant };
  }, [startDate, method]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Pregnancy</span>
        <h2 className="tool-live-title">{config?.heading ?? 'Due date calculator'}</h2>
        <p className="tool-live-subtitle">
          Estimate a pregnancy due date from your last period, conception date, or IVF transfer
          date, using standard obstetric dating conventions.
        </p>
      </div>

      <div className="tool-live-grid">
        <section
          className="tool-live-uploader"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}
        >
          <label className="tool-live-field tool-live-field-full">
            <span>Calculate from</span>
            <select onChange={(e) => onChange(setMethod)(e.target.value as Method)} value={method}>
              {(Object.keys(METHOD_LABELS) as Method[]).map((m) => (
                <option key={m} value={m}>
                  {METHOD_LABELS[m]}
                </option>
              ))}
            </select>
          </label>
          <label className="tool-live-field">
            <span>{METHOD_LABELS[method]}</span>
            <input
              onChange={(e) => onChange(setStartDate)(e.target.value)}
              type="date"
              value={startDate}
            />
          </label>
        </section>

        <section className="tool-live-preview">
          {result ? (
            <>
              <p className="tool-live-panel-note">
                <strong>{formatDate(result.dueDate)}</strong>
                <span>Estimated due date</span>
              </p>
              <p className="tool-live-panel-note">
                <strong>
                  {result.weeksPregnant} weeks, {result.daysRemainder} days
                </strong>
                <span>Today, trimester {result.trimester}</span>
              </p>
              <p className="tool-live-panel-note">
                This is an estimate using standard dating rules (last period + 280 days). Only about
                1 in 20 babies arrive on the exact due date; a full-term birth anywhere from 3 weeks
                before to 2 weeks after is normal. This is not medical advice, an ultrasound-based
                estimate from your doctor or midwife is more accurate.
              </p>
            </>
          ) : (
            <p className="tool-live-panel-note">Enter a valid date to see the estimate.</p>
          )}
        </section>
      </div>
    </div>
  );
}
